import api from '../api';
import type { InventoryItem, InventoryItemFormData, StockMovement } from '../../types/inventory';
import { cache } from '../../utils/cache';
import { mapDtoToInventoryItem, mapDtoToStockMovement, mapFormDataToDto } from './inventoryMappers';

export const inventoryItemsService = {
  getAll: async (opts?: { includeOnboardingSamples?: boolean }): Promise<InventoryItem[]> => {
    const includeSamples = opts?.includeOnboardingSamples === true;
    const fetcher = async () => {
      const params: Record<string, string> = { status: 'all' };
      if (includeSamples) params.include_onboarding_samples = 'true';
      const response = await api.get('/inventory', { params });
      return (response.data || []).map(mapDtoToInventoryItem);
    };
    // Onboarding sample data is volatile (seeded/cleaned mid-tour); never serve the
    // sample-inclusive list from the 5-min cache, or a fetch that ran before the
    // seed would strand the pickers with a stale sample-less list.
    if (includeSamples) return fetcher();
    return cache.fetch('inventory_all', fetcher);
  },

  getById: async (id: string): Promise<InventoryItem> => {
    return cache.fetch(`inventory_${id}`, async () => {
      const response = await api.get(`/inventory/${id}`);
      return mapDtoToInventoryItem(response.data);
    });
  },

  create: async (data: InventoryItemFormData): Promise<InventoryItem> => {
    const requestData = mapFormDataToDto(data);
    const response = await api.post('/inventory', requestData);
    cache.invalidate('inventory');
    cache.invalidate('reports_');
    return mapDtoToInventoryItem(response.data);
  },

  update: async (id: string, data: InventoryItemFormData): Promise<InventoryItem> => {
    const requestData = mapFormDataToDto(data);
    await api.put(`/inventory/${id}`, requestData);
    cache.delete(`inventory_${id}`);
    cache.delete('inventory_all');
    cache.invalidate('reports_');
    const refreshed = await api.get(`/inventory/${id}`);
    return mapDtoToInventoryItem(refreshed.data);
  },

  delete: async (id: string): Promise<void> => {
    await api.delete(`/inventory/${id}`);
    cache.delete(`inventory_${id}`);
    cache.delete('inventory_all');
    cache.invalidate('reports_');
  },

  getFinishedGoodsItems: async (opts?: { includeOnboardingSamples?: boolean }): Promise<InventoryItem[]> => {
    try {
      const response = await api.get('/inventory', {
        params: {
          item_type: 'finished_good',
          status: 'active',
          limit: 200,
          order_by: 'name',
          // The practice packing step filters this list down to the tagged
          // samples, so the samples have to be in it in the first place.
          ...(opts?.includeOnboardingSamples ? { include_onboarding_samples: 'true' } : {}),
        },
      });
      const data = response.data?.data || response.data || [];
      return Array.isArray(data) ? data.map(mapDtoToInventoryItem) : [];
    } catch (error) {
      console.error('Error fetching finished goods items:', error);
      return [];
    }
  },

  adjustStock: async (id: string, quantity: number, type: 'add' | 'remove', notes: string, lotId?: string): Promise<void> => {
    const payload: Record<string, unknown> = { quantity, type, notes };
    if (typeof lotId === 'string' && lotId.trim() !== '') {
      payload.lot_id = lotId;
    }
    await api.post(`/inventory/${id}/adjust`, payload);
    cache.delete(`inventory_${id}`);
    cache.delete('inventory_all');
    cache.invalidate('reports_');
  },

  getHistory: async (id: string, page = 1, limit = 20, lotId?: string): Promise<{ data: StockMovement[]; total: number }> => {
    const offset = (page - 1) * limit;
    const response = await api.get(`/inventory/${id}/history`, {
      params: { limit, offset, lot_id: lotId },
    });
    return {
      data: (response.data.data || []).map(mapDtoToStockMovement),
      total: response.data.meta.total,
    };
  },

  getMovementById: async (id: string): Promise<StockMovement> => {
    const response = await api.get(`/inventory/movements/${id}`);
    return mapDtoToStockMovement(response.data);
  },

  updateMovement: async (id: string, data: { date: string; notes: string; unit_cost: number }): Promise<void> => {
    await api.put(`/inventory/movements/${id}`, data);
  },

  uploadImage: async (id: string, file: File): Promise<{ path: string; message: string }> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post(`/uploads/inventory/${id}/image`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },

  deleteImage: async (id: string): Promise<{ message: string }> => {
    const response = await api.delete(`/uploads/inventory/${id}/image`);
    return response.data;
  },

  downloadGreenBeanImportTemplate: async (): Promise<void> => {
    const response = await api.get('/inventory/import/green-beans/template', {
      responseType: 'blob',
    });
    const blob = new Blob([response.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'green-bean-import-template.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  },

  importGreenBeans: async (
    rows: Array<{
      bean_name: string;
      description: string;
      processor_name: string;
      species: string;
      variety: string;
      process_type: string;
      initial_qty: number;
      reorder_point: number;
      standard_cost: number;
    }>,
  ): Promise<{ created: number; item_ids: string[] }> => {
    const response = await api.post('/inventory/import/green-beans', { rows });
    cache.invalidate('inventory');
    cache.invalidate('reports_');
    return response.data;
  },
};
